import { create } from '@bufbuild/protobuf'
import { t } from 'i18next'

import { Contract } from '@enechain/eposition-proto/bffcommon/v1/eex_model_pb'
import {
  FloatingIndexSchema,
  Leg,
  LegSchema,
  TransactionSchema as TransactionSchemaProto,
} from '@enechain/eposition-proto/external/v1/model_pb'
import {
  DealPosition,
  DeliveryType,
  ProductSpreadType,
  SpreadType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import {
  SpreadTradeSchema,
  getDeliveryEndDateTimestamp,
} from '@enechain/eposition-ui'
import { formatDateToJSTYYYYMMddTimeStamp } from '@enechain/eposition-utils/src/date'

import {
  reconstructTransactions,
  validateSimulationOrderPatternModel,
} from '~/pages/Simulation/models/SimulationOrderPatternHelper'
import {
  TransactionSchema,
  ValidatedOrderPatternSchema,
} from '~/pages/Simulation/schemas'

export type SimulationOrderPatternModel = {
  orderNumber: number
  isPhysicalFuture: boolean
  selfDeliveryType: DeliveryType
  eClearDeliveryType: DeliveryType
  spreadType: SpreadTradeSchema
  dealPosition: DealPosition
  baseSummaryOrder: TransactionSchema
  exchangeSummaryOrder?: TransactionSchema
  baseTransactions: TransactionSchema[]
  exchangeTransactions?: TransactionSchema[]
}

type Result = {
  model?: SimulationOrderPatternModel
  error?: string
}

export function convertLegsFromOrderPatternModel(
  order: SimulationOrderPatternModel,
): Leg[] {
  const baseLegs = order.baseTransactions.map((transaction) =>
    create(LegSchema, {
      spreadType: order.spreadType.isSpreadTrade
        ? SpreadType.SPREAD_BASE
        : SpreadType.UNSPECIFIED,
      transactions: [
        create(TransactionSchemaProto, {
          organizationId: MOCK_ORGANIZATION_ID,
          unitPricePerKwh: { value: transaction.unitPricePerKwh },
          volumeInKw: (Number(transaction.volumeInKw) * 1000).toString(),
          deliveryStartAt:
            transaction.deliveryDateRange.deliveryStartDate !== undefined
              ? formatDateToJSTYYYYMMddTimeStamp(
                  transaction.deliveryDateRange.deliveryStartDate,
                )
              : undefined,
          deliveryEndAt: getDeliveryEndDateTimestamp(
            transaction.deliveryDateRange.deliveryEndDate,
          ),
          deliveryType: order.selfDeliveryType,
          eclearDeliveryType: order.eClearDeliveryType,
          areaCode: transaction.areaCode,
          dealPosition: order.dealPosition,
          deliveryHourType: transaction.deliveryHourType,
          deliveryPeriodType: transaction.deliveryPeriodType,
          floatingIndex:
            transaction.floatingIndexCode != null &&
            transaction.floatingIndexCode.trim() !== ''
              ? create(FloatingIndexSchema, {
                  code: transaction.floatingIndexCode,
                })
              : undefined,
        }),
      ],
    }),
  )

  const exchangeLegs =
    order.exchangeTransactions?.map((transaction) =>
      create(LegSchema, {
        spreadType: SpreadType.SPREAD_EXCHANGE,
        transactions: [
          create(TransactionSchemaProto, {
            organizationId: MOCK_ORGANIZATION_ID,
            unitPricePerKwh: { value: transaction.unitPricePerKwh },
            volumeInKw: (Number(transaction.volumeInKw) * 1000).toString(),
            deliveryStartAt:
              transaction.deliveryDateRange.deliveryStartDate !== undefined
                ? formatDateToJSTYYYYMMddTimeStamp(
                    transaction.deliveryDateRange.deliveryStartDate,
                  )
                : undefined,
            deliveryEndAt: getDeliveryEndDateTimestamp(
              transaction.deliveryDateRange.deliveryEndDate,
            ),
            deliveryType: order.selfDeliveryType,
            eclearDeliveryType: order.eClearDeliveryType,
            areaCode: transaction.areaCode,
            dealPosition:
              order.dealPosition === DealPosition.BID
                ? DealPosition.OFFER
                : DealPosition.BID,
            deliveryHourType: transaction.deliveryHourType,
            deliveryPeriodType: transaction.deliveryPeriodType,
            floatingIndex:
              transaction.floatingIndexCode != null &&
              transaction.floatingIndexCode.trim() !== ''
                ? create(FloatingIndexSchema, {
                    code: transaction.floatingIndexCode,
                  })
                : undefined,
          }),
        ],
      }),
    ) ?? []

  return [...baseLegs, ...exchangeLegs]
}

export function getSpreadType(
  modal: SimulationOrderPatternModel,
): ProductSpreadType {
  if (!modal.spreadType.isSpreadTrade) {
    return ProductSpreadType.UNSPECIFIED
  }

  const uniqueTypes = Array.from(new Set(modal.spreadType.spreadType)).sort()

  if (uniqueTypes.length === 0) {
    return ProductSpreadType.UNSPECIFIED
  }

  switch (uniqueTypes.length) {
    case 1:
      return uniqueTypes[0]
    case 2:
      if (
        uniqueTypes.includes(ProductSpreadType.AREA) &&
        uniqueTypes.includes(ProductSpreadType.DELIVERY_PERIOD)
      ) {
        return ProductSpreadType.AREA_DELIVERY_PERIOD
      }
      if (
        uniqueTypes.includes(ProductSpreadType.AREA) &&
        uniqueTypes.includes(ProductSpreadType.HOUR_TYPE)
      ) {
        return ProductSpreadType.AREA_HOUR_TYPE
      }
      if (
        uniqueTypes.includes(ProductSpreadType.DELIVERY_PERIOD) &&
        uniqueTypes.includes(ProductSpreadType.HOUR_TYPE)
      ) {
        return ProductSpreadType.DELIVERY_PERIOD_HOUR_TYPE
      }
      break
    case 3:
      if (
        uniqueTypes.includes(ProductSpreadType.AREA) &&
        uniqueTypes.includes(ProductSpreadType.DELIVERY_PERIOD) &&
        uniqueTypes.includes(ProductSpreadType.HOUR_TYPE)
      ) {
        return ProductSpreadType.AREA_DELIVERY_PERIOD_HOUR_TYPE
      }
      break
  }

  return ProductSpreadType.UNSPECIFIED
}

export function convertSimulationOrderPatternModel(
  value: ValidatedOrderPatternSchema,
  orderIndex: number,
  eexContracts: Contract[] = [],
): Result {
  const model = {
    orderNumber: orderIndex + 1,
    isPhysicalFuture: value.isPhysicalFuture,
    selfDeliveryType: value.selfDeliveryType,
    eClearDeliveryType: value.isPhysicalFuture
      ? value.eClearDeliveryType
      : value.selfDeliveryType,
    spreadType: value.spreadType,
    dealPosition: value.dealPosition,
    baseSummaryOrder: value.baseSummaryOrder,
    exchangeSummaryOrder: value.exchangeSummaryOrder,
    baseTransactions: [] as TransactionSchema[],
    exchangeTransactions: undefined as TransactionSchema[] | undefined,
  }
  const baseResult = reconstructTransactions(
    value.baseSummaryOrder,
    value.baseTransactions ?? [],
    value.isPhysicalFuture,
    eexContracts,
  )
  const errorPrefix = `${t('page:simulation.input_order')}${orderIndex + 1}:`
  if (baseResult.error !== undefined) {
    return {
      model: undefined,
      error: `${errorPrefix} ${baseResult.error}`,
    }
  }

  if (
    value.spreadType.isSpreadTrade &&
    value.exchangeSummaryOrder !== undefined
  ) {
    const exchangeResult = reconstructTransactions(
      value.exchangeSummaryOrder,
      value.exchangeTransactions ?? [],
      value.isPhysicalFuture,
      eexContracts,
    )
    if (exchangeResult.error !== undefined) {
      return {
        model: undefined,
        error: `${errorPrefix} ${exchangeResult.error}`,
      }
    }
    model.exchangeTransactions = exchangeResult.transactions
  }

  model.baseTransactions = baseResult.transactions

  const { errorMessage } = validateSimulationOrderPatternModel(model)
  if (errorMessage !== undefined) {
    return {
      model: undefined,
      error: `${errorPrefix} ${errorMessage}`,
    }
  }
  return {
    model,
    error: undefined,
  }
}

export const MOCK_ORGANIZATION_ID = '00000000-0000-0000-0000-000000000000'
export const MOCK_PORTFOLIO_ID = 'b1250ca6-3e59-4b2e-874d-eb5a6fcd2987'
